import $k30rM$react, {createContext as $k30rM$createContext, forwardRef as $k30rM$forwardRef, useContext as $k30rM$useContext, useCallback as $k30rM$useCallback, useMemo as $k30rM$useMemo} from "react";

/*
 * Copyright 2024 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */ 
const $f9554a667e4f0374$export$d188a835a7bc5783 = /*#__PURE__*/ (0, $k30rM$createContext)({});
const $f9554a667e4f0374$export$f55761759794cf55 = /*#__PURE__*/ (0, $k30rM$createContext)(null);
const $f9554a667e4f0374$export$62ed72bc21f6b8a6 = /*#__PURE__*/ (0, $k30rM$forwardRef)(function DropIndicator(props, ref) {
    let { render: render } = (0, $k30rM$useContext)($f9554a667e4f0374$export$f55761759794cf55);
    return /*#__PURE__*/ (0, $k30rM$react).createElement((0, $k30rM$react).Fragment, null, render(props, ref));
});
function $f9554a667e4f0374$export$971707d8a129a1f7(dragAndDropHooks, dropState) {
    let renderDropIndicator = dragAndDropHooks?.renderDropIndicator;
    let isVirtualDragging = dragAndDropHooks?.isVirtualDragging?.();
    let fn = (0, $k30rM$useCallback)((target)=>{
        // Only show drop indicators when virtual dragging or this is the current drop target.
        if (isVirtualDragging || dropState?.isDropTarget(target)) return renderDropIndicator ? renderDropIndicator(target) : /*#__PURE__*/ (0, $k30rM$react).createElement($f9554a667e4f0374$export$62ed72bc21f6b8a6, {
            target: target
        });
    // We invalidate whenever the target changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dropState?.target,
        isVirtualDragging,
        renderDropIndicator
    ]);
    return dragAndDropHooks?.useDropIndicator ? fn : undefined;
}
function $f9554a667e4f0374$export$d1e8e3fbb7461f6(selectionManager, dragAndDropHooks, dropState) {
    // Persist the focused key and the drop target key.
    let focusedKey = selectionManager.focusedKey;
    let dropTargetKey = null;
    if (dragAndDropHooks?.isVirtualDragging?.() && dropState?.target?.type === 'item') {
        dropTargetKey = dropState.target.key;
        if (dropState.target.dropPosition === 'after') {
            // Normalize to the "before" drop position since we only render those to the DOM.
            let nextKey = dropState.collection.getKeyAfter(dropTargetKey);
            let lastDescendantKey = null;
            if (nextKey != null) {
                let targetLevel = dropState.collection.getItem(dropTargetKey)?.level ?? 0;
                // Skip over any rows that are descendants of the target ("after" position should be after all children)
                while(nextKey){
                    let node = dropState.collection.getItem(nextKey);
                    // eslint-disable-next-line max-depth
                    if (!node) break;
                    // Skip over non-item nodes (e.g., loaders) since they can't be drop targets.
                    // eslint-disable-next-line max-depth
                    if (node.type !== 'item') {
                        nextKey = dropState.collection.getKeyAfter(nextKey);
                        continue;
                    }
                    // Stop once we find an item at the same level or higher
                    // eslint-disable-next-line max-depth
                    if ((node.level ?? 0) <= targetLevel) break;
                    lastDescendantKey = nextKey;
                    nextKey = dropState.collection.getKeyAfter(nextKey);
                }
            }
            // If nextKey is null (end of collection), use the last descendant
            dropTargetKey = nextKey ?? lastDescendantKey ?? dropTargetKey;
        }
    }
    return (0, $k30rM$useMemo)(()=>{
        return new Set([
            focusedKey,
            dropTargetKey
        ].filter((k)=>k != null));
    }, [
        focusedKey,
        dropTargetKey
    ]);
}


export {$f9554a667e4f0374$export$d188a835a7bc5783 as DragAndDropContext, $f9554a667e4f0374$export$f55761759794cf55 as DropIndicatorContext, $f9554a667e4f0374$export$62ed72bc21f6b8a6 as DropIndicator, $f9554a667e4f0374$export$971707d8a129a1f7 as useRenderDropIndicator, $f9554a667e4f0374$export$d1e8e3fbb7461f6 as useDndPersistedKeys};
//# sourceMappingURL=DragAndDrop.mjs.map
